import { LoadingOverlay, Stack, Text, TextInput } from '@mantine/core';
import { useEffect, useState } from 'react';
import CustomDropZone from '@/components/CustomDropZone.tsx';
import StyledModal from '@/components/StyledModal/StyledModal.tsx';
import ModalConfirmationButtons from '@/components/StyledModal/ModalConfirmationButtons.tsx';
import useReplaceStaticDataset from '@/hooks/api/datasets/static/useReplaceStaticDataset.ts';
import FileItem from '@/pages/AutomationPage/components/ManualRunModal/components/FileItem.tsx';
import { RelativeFile } from '@/types/common.ts';
import { StaticDataset } from '@/types/datasets.ts';
import { useEditVersionContext } from '@/context/EditVersionContext/useEditVersionContext.ts';

type Props = {
  opened: boolean;
  onClose: () => void;
  staticDataset?: StaticDataset;
  sourceName?: string;
};

const ReplaceStaticDatasetModal = ({ opened, onClose, staticDataset, sourceName }: Props) => {
  const editor = useEditVersionContext();
  const currentName =
    sourceName ?? staticDataset?.available_dataset?.name ?? staticDataset?.dataset.name ?? '';
  const [datasetName, setDatasetName] = useState('');
  const [files, setFiles] = useState<RelativeFile[]>([]);

  const handleClose = () => {
    setFiles([]);
    setDatasetName(currentName);
    onClose();
  };

  const { mutate: replaceStaticDataset, isPending } = useReplaceStaticDataset({
    onSuccess: handleClose,
  });

  useEffect(() => {
    if (opened) {
      setDatasetName(currentName);
      setFiles([]);
    }
  }, [opened, currentName]);

  const confirmDisabled =
    !staticDataset ||
    files.length === 0 ||
    !datasetName.trim() ||
    editor.hasPendingEdits ||
    !!editor.staleDraft;

  return (
    <StyledModal opened={opened} onClose={handleClose} title="Replace data source">
      <Stack gap="sm" style={{ position: 'relative' }}>
        <LoadingOverlay visible={isPending} zIndex={1000} />

        <Text size="sm" c="dimmed">
          After processing, this file updates the latest draft wherever it is used. The published
          automation keeps its existing data until you publish.
        </Text>
        {(editor.hasPendingEdits || editor.staleDraft) && (
          <Text size="sm">Wait for edits to save or reload the editor before replacing data.</Text>
        )}
        <TextInput
          label="Data Source Name"
          placeholder="Data source name"
          size="sm"
          value={datasetName}
          onChange={(event) => setDatasetName(event.currentTarget.value)}
          autoFocus={true}
          data-autofocus
          required={true}
          readOnly={isPending}
        />

        <CustomDropZone mode="multiple" files={files} setFiles={setFiles} height="60px" />

        {files.map((file, idx) => (
          <FileItem
            key={`${file.path}-${idx}`}
            name={file.path}
            size={file.file.size}
            onDelete={() => {
              const newFiles = [...files];
              newFiles.splice(idx, 1);
              setFiles(newFiles);
            }}
          />
        ))}
      </Stack>
      <ModalConfirmationButtons
        confirmText="Replace"
        confirmLoading={isPending}
        confirmDisabled={confirmDisabled}
        closeOnConfirm={false}
        onClose={handleClose}
        onConfirm={() => {
          if (!staticDataset) {
            return;
          }
          replaceStaticDataset({
            staticDatasetId: staticDataset.id,
            name: datasetName,
            files: [...files],
          });
        }}
      />
    </StyledModal>
  );
};

export default ReplaceStaticDatasetModal;
